import { getContext, onDestroy, setContext, untrack } from 'svelte';
import { SvelteMap } from 'svelte/reactivity';

const coordinatorKey = Symbol('modal-coordinator');
const presentationKey = Symbol('modal-presentation');

export const MODAL_PRIORITY = { normal: 0, prerequisite: 100 } as const;

/** One visible modal per mounted scope. Equal priorities wait in request order. */
export function createModalCoordinator() {
  const requests = new SvelteMap<symbol, { priority: number; sequence: number }>();
  let sequence = 0;
  function activeModal() {
    let winner: symbol | null = null;
    let selected: { priority: number; sequence: number } | undefined;
    for (const [id, request] of requests) {
      if (!selected || request.priority > selected.priority
        || request.priority === selected.priority && request.sequence < selected.sequence) {
        winner = id;
        selected = request;
      }
    }
    return winner;
  }

  return {
    get active() { return activeModal(); },
    request(id: symbol, open: boolean, priority: number = MODAL_PRIORITY.normal) {
      if (!open) { requests.delete(id); return; }
      const previous = requests.get(id);
      if (previous?.priority === priority) return;
      requests.set(id, { priority, sequence: previous?.sequence ?? sequence++ });
    },
    release(id: symbol) { requests.delete(id); }
  };
}

export type ModalCoordinator = ReturnType<typeof createModalCoordinator>;

export function getModalCoordinator() {
  return getContext<ModalCoordinator | undefined>(coordinatorKey);
}

export function provideModalCoordinator(coordinator = createModalCoordinator()) {
  setContext(coordinatorKey, coordinator);
  return coordinator;
}

export type ModalPresentation = {
  readonly coordinated: boolean;
  readonly requestedOpen: boolean;
  readonly open: boolean;
  readonly priority: number;
  readonly suppressCloseFocus: boolean;
};

export function useCoordinatedModal(getOpen: () => boolean, getPriority: () => number | undefined): ModalPresentation {
  const coordinator = getModalCoordinator();
  const parent = getContext<ModalPresentation | undefined>(presentationKey);
  const id = Symbol('modal');
  const priority = () => getPriority() ?? (parent ? parent.priority + 1 : MODAL_PRIORITY.normal);
  const presentation: ModalPresentation = {
    coordinated: coordinator !== undefined,
    get requestedOpen() { return getOpen(); },
    get open() { return getOpen() && (!coordinator || coordinator.active === id); },
    get priority() { return priority(); },
    get suppressCloseFocus() {
      return !!coordinator && (getOpen() || coordinator.active !== null && coordinator.active !== id);
    }
  };

  if (coordinator) {
    $effect(() => {
      const open = getOpen();
      const currentPriority = priority();
      untrack(() => coordinator.request(id, open, currentPriority));
    });
    onDestroy(() => coordinator.release(id));
  }
  setContext(presentationKey, presentation);
  return presentation;
}

export function getModalPresentation() {
  return getContext<ModalPresentation | undefined>(presentationKey);
}

/** Hide a suspended surface immediately, including its closing animation. */
export function modalPresentationStyle(modal: ModalPresentation | undefined, style?: string | null) {
  if (!modal?.coordinated || modal.open) return style;
  const existing = style?.replace(/^[;\s]+|[;\s]+$/g, '');
  return `${existing ? `${existing};` : ''}display:none!important;`;
}
